import React, { useState } from 'react';
import { Mail, Lock, User, Phone, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, analytics, db } from './firebase';
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signInWithPopup, GoogleAuthProvider
} from 'firebase/auth';
import { logEvent } from 'firebase/analytics';
import { serverTimestamp, doc, setDoc } from 'firebase/firestore';
import { saveUserProfile } from './services/firestoreService';
import { setPlanCache } from './utils/planCache';

const googleProvider = new GoogleAuthProvider();

// Save user profile to Firestore with affiliate fields
const createUserDoc = async (firebaseUser, extraFields = {}) => {
  const uid = firebaseUser.uid;
  const referralCode = 'TF' + uid.slice(0, 6).toUpperCase();
  const profile = {
    uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName || extraFields.name || '',
    photoURL: firebaseUser.photoURL || null,
    plan: 'free',
    createdAt: serverTimestamp(),
    referralCode,
    affiliateEarnings: 0,
    pendingPayout: 0,
    totalReferrals: 0,
    ...extraFields,
  };
  await saveUserProfile(uid, profile);
  return profile;
};

// Save referral if ?ref= query param exists
const saveReferral = async (uid, refCode) => {
  if (!refCode) return;
  try {
    await setDoc(doc(db, 'referrals', uid), {
      referredBy: refCode,
      referredAt: serverTimestamp(),
      uid,
    });
  } catch (e) {
    console.warn('Referral save failed:', e);
  }
};

const Auth = ({ onLogin, mode = 'login' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email required है';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Valid email enter करें';
    }

    if (!formData.password) {
      newErrors.password = 'Password required है';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimum 6 characters का होना चाहिए';
    }

    if (!isLogin) {
      if (!formData.name) {
        newErrors.name = 'Name required है';
      } else if (formData.name.trim().length < 3) {
        newErrors.name = 'Name minimum 3 characters का होना चाहिए';
      }
      if (!formData.mobile) {
        newErrors.mobile = 'Mobile number required है';
      } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
        newErrors.mobile = 'Valid 10-digit mobile number enter करें (6/7/8/9 से शुरू)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleMobileChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, mobile: digits });
    if (errors.mobile) {
      setErrors({ ...errors, mobile: '' });
    }
  };

  const formatMobileDisplay = (mobile) => {
    if (mobile.length === 10) return `+91-${mobile.slice(0, 5)}-${mobile.slice(5)}`;
    if (mobile.length > 0) return `+91-${mobile}`;
    return '';
  };

  // Google Sign-In
  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;
      const refCode = new URLSearchParams(location.search).get('ref');

      // Create/merge user doc (merge: true in saveUserProfile won't overwrite existing)
      await createUserDoc(firebaseUser, { name: firebaseUser.displayName || '' });
      if (refCode) await saveReferral(firebaseUser.uid, refCode);

      logEvent(analytics, 'user_login', { method: 'google' });
      // onAuthStateChanged in App.js handles redirect
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setMessage({ type: 'error', text: err.message });
      }
    }
  };

  // Firebase login
  const handleLogin = async () => {
    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      logEvent(analytics, 'user_login', { method: 'email' });
      setMessage({ type: 'success', text: 'Login successful!' });
      // onLogin is called by App.js via onAuthStateChanged
    } catch (err) {
      const msg = err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password'
        ? 'Invalid email या password!'
        : err.message;
      setMessage({ type: 'error', text: msg });
    }
  };

  // Firebase signup
  const handleSignup = async () => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const firebaseUser = userCredential.user;
      const refCode = new URLSearchParams(location.search).get('ref');

      await createUserDoc(firebaseUser, {
        name: formData.name.trim(),
        mobile: `+91${formData.mobile}`,
        mobileVerified: false,
        signupDate: serverTimestamp(),
        lastLogin: serverTimestamp(),
      });
      if (refCode) await saveReferral(firebaseUser.uid, refCode);
      setPlanCache('free'); // new users always start on free plan

      setMessage({ type: 'success', text: 'Account created successfully!' });
      // onLogin is called by App.js via onAuthStateChanged
    } catch (err) {
      const msg = err.code === 'auth/email-already-in-use'
        ? 'Email already registered है!'
        : err.message;
      setMessage({ type: 'error', text: msg });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    if (validateForm()) {
      if (isLogin) {
        handleLogin();
      } else {
        handleSignup();
      }
    }
  };

  const toggleMode = () => {
    const nextIsLogin = !isLogin;
    setIsLogin(nextIsLogin);
    setFormData({ name: '', email: '', mobile: '', password: '' });
    setErrors({});
    setMessage({ type: '', text: '' });
    navigate(nextIsLogin ? '/login' : '/signup');
  };

  return (
    <div className="auth-page min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #22C55E)' }}>
              <span style={{ color: '#fff' }} className="font-bold text-lg">T</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>TradeFix AI</h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>AI-Powered Trading Psychology Coach</p>
        </div>

        {/* Auth Card */}
        <div className="rounded-2xl p-8" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
            {isLogin ? 'Welcome Back!' : 'Create Account'}
          </h2>

          {/* Google Sign-In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 py-3 rounded-xl font-semibold mb-4 transition-all"
            style={{ background: '#fff', color: '#1f2328', border: '1px solid #d0d7de' }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>or continue with email</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
          </div>

          {/* Message Display */}
          {message.text && (
            <div className={`mb-4 p-4 rounded-xl flex items-center space-x-2 ${
              message.type === 'success'
                ? 'bg-green-900/30 text-green-300 border border-green-700/50'
                : 'bg-red-900/30 text-red-300 border border-red-700/50'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle size={20} />
              ) : (
                <AlertCircle size={20} />
              )}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Field (Signup only) */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      errors.name ? 'border-red-500' : 'border-gray-700'
                    }`}
                    placeholder="Enter your name"
                  />
                </div>
                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
              </div>
            )}

            {/* Mobile Field (Signup only) */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Mobile Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleMobileChange}
                    className={`w-full pl-10 pr-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      errors.mobile ? 'border-red-500' : 'border-gray-700'
                    }`}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                  />
                </div>
                {formData.mobile.length > 0 && (
                  <p className="text-blue-400 text-sm mt-1 font-medium">{formatMobileDisplay(formData.mobile)}</p>
                )}
                {errors.mobile && <p className="text-red-400 text-xs mt-1">{errors.mobile}</p>}
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.email ? 'border-red-500' : 'border-gray-700'
                  }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.password ? 'border-red-500' : 'border-gray-700'
                  }`}
                  placeholder="Enter your password"
                />
              </div>
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white py-3 rounded-xl font-semibold shadow-lg shadow-blue-500/20 transition-all"
            >
              {isLogin ? 'Login' : 'Create Account'}
            </button>
          </form>

          {/* Toggle Link */}
          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                onClick={toggleMode}
                className="font-medium transition-colors"
                style={{ color: 'var(--accent)' }}
              >
                {isLogin ? 'Sign Up' : 'Login'}
              </button>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs mt-6" style={{ color: 'var(--text-muted)' }}>
          By continuing, you agree to TradeFix AI's Terms & Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Auth;
